/** @jsxRuntime classic */
/** @jsx jsx */
import { css, keyframes, jsx } from '@emotion/core';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { websitesApi } from 'src/api_services/websites';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { GitDeployState } from 'src/orchd-client';
import { getErrorInfo } from 'src/utils/errors';
import { useToast } from 'src/utils/toast/useToast';
import { zIndex } from 'src/utils/zIndex';

export type GithubStepKey = 'createWebsite' | 'connectRepo' | 'installApp' | 'triggerDeploy';

export type GithubStepStatus = 'pending' | 'running' | 'done' | 'error';

export interface GithubStep {
  key: GithubStepKey;
  label: ReactNode;
  status: GithubStepStatus;
  error?: string;
}

interface Props {
  steps?: GithubStep[];
  deployLog?: string;
}

// Benevolent deception: a call that settles almost instantly would flash
// its "running" line away before it can be read, making the log jitter.
// Hold every step in the running state for at least this long.
const GITHUB_STEP_MIN_VISIBLE_MS = 500;

// How long a failed step stays on screen before the caller's shared
// error handling (toast + form restore) takes over again.
const GITHUB_STEP_ERROR_DWELL_MS = 1800;

// Deployment follow-up: how often the deploy list and the deploy log are
// re-fetched while following the fresh deployment, how long we wait for
// the triggered deployment to show up in the list at all, the overall
// watch budget, and how long the finished result stays on screen before
// the caller dismisses the overlay by navigating on.
const DEPLOY_POLL_MS = 3_000;
const DEPLOY_DISCOVERY_TIMEOUT_MS = 30_000;
const DEPLOY_FOLLOW_TIMEOUT_MS = 15 * 60 * 1000;
const DEPLOY_RESULT_DISMISS_MS = 5_000;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export interface GithubDeployStepsInit {
  withCreateWebsite: boolean;
  domain?: string;
  repoFullName: string;
  withApp: boolean;
  /** trigger the initial deploy only when the user selected auto deployment */
  withAutoDeploy: boolean;
}

/**
 * Tracks the real API calls of a github deploy flow (create website,
 * connect repository, optionally create the persistent app, trigger the
 * first deployment) so the AddWebsiteGithubProgress overlay can report
 * which call is in flight and its actual outcome. Shared by the
 * add-website github flow and the projects DeployToWebsite modal.
 */
export const useGithubDeploySteps = () => {
  const [steps, setSteps] = useState<GithubStep[] | undefined>(undefined);

  const [deployLog, setDeployLog] = useState('');

  const toast = useToast();

  const initSteps = (opts: GithubDeployStepsInit) => {
    setDeployLog('');

    const built: GithubStep[] = [];

    if (opts.withCreateWebsite) {
      built.push({
        key: 'createWebsite',
        status: 'pending',
        label: (
          <FormattedMessage
            id="websites.add_website.github_progress.create_website"
            defaultMessage="Creating website {domain}"
            values={{ domain: opts.domain ?? '' }}
          />
        ),
      });
    }

    built.push({
      key: 'connectRepo',
      status: 'pending',
      label: (
        <FormattedMessage
          id="websites.add_website.github_progress.connect_repo"
          defaultMessage="Connecting GitHub repository {repo}"
          values={{ repo: opts.repoFullName }}
        />
      ),
    });

    if (opts.withApp) {
      built.push({
        key: 'installApp',
        status: 'pending',
        label: (
          <FormattedMessage
            id="websites.add_website.github_progress.install_app"
            defaultMessage="Creating worker process"
          />
        ),
      });
    }

    if (opts.withAutoDeploy) {
      built.push({
        key: 'triggerDeploy',
        status: 'pending',
        label: (
          <FormattedMessage
            id="websites.add_website.github_progress.trigger_deploy"
            defaultMessage="Deploying the site from GitHub"
          />
        ),
      });
    }

    setSteps(built);
  };

  const patchStep = (key: GithubStepKey, patch: Partial<GithubStep>) => {
    setSteps((prev) => prev?.map((step) => (step.key === key ? { ...step, ...patch } : step)));
  };

  // run one API call of the flow, reporting its real outcome to the
  // overlay before rethrowing to the caller's shared error handling
  const runStep = useCallback(async <T,>(key: GithubStepKey, fn: () => Promise<T>): Promise<T> => {
    const startedAt = Date.now();
    patchStep(key, { status: 'running', error: undefined });

    try {
      const result = await fn();
      // keep the running line visible long enough to be read, even for
      // calls that settle almost instantly
      const elapsed = Date.now() - startedAt;

      if (elapsed < GITHUB_STEP_MIN_VISIBLE_MS) {
        await delay(GITHUB_STEP_MIN_VISIBLE_MS - elapsed);
      }

      patchStep(key, { status: 'done' });
      return result;
    } catch (e) {
      const { message } = getErrorInfo(e);
      patchStep(key, { status: 'error', error: message });
      // hold the overlay briefly so the failed step stays readable
      // before the shared error handling restores the form
      await delay(GITHUB_STEP_ERROR_DWELL_MS);
      throw e;
    }
  }, []);

  // trigger the first deployment and follow it to its outcome: the
  // triggerDeploy step stays running while the deployment itself runs,
  // streaming its log into the overlay, and settles only after the result
  // has been on screen for a few seconds. Returns whether the deployment
  // completed successfully; a deployment we could not observe at all
  // counts as success so the flow is not blocked on a broken follow-up
  // (the /git page tracks in-flight deployments and toasts their outcome
  // by itself)
  const triggerAndFollowDeploy = useCallback(
    async ({ orgId, websiteId }: { orgId: string; websiteId: string }) => {
      patchStep('triggerDeploy', { status: 'running', error: undefined });
      setDeployLog('');

      const knownDeployIds = new Set<string>();

      try {
        const { data } = await websitesApi.getWebsiteGitDeploys({ orgId, websiteId });
        data.forEach((deployment) => knownDeployIds.add(deployment.deployId));
      } catch {
        // treated as "everything in the list is new" below
      }

      try {
        await websitesApi.deployWebsiteGithubNow({ orgId, websiteId });
      } catch (e) {
        const { message } = getErrorInfo(e);
        patchStep('triggerDeploy', { status: 'error', error: message });
        await delay(GITHUB_STEP_ERROR_DWELL_MS);
        throw e;
      }

      let deployId: string | undefined;

      const discoveryDeadline = Date.now() + DEPLOY_DISCOVERY_TIMEOUT_MS;

      while (!deployId && Date.now() < discoveryDeadline) {
        await delay(DEPLOY_POLL_MS);

        try {
          const { data } = await websitesApi.getWebsiteGitDeploys({ orgId, websiteId });
          // the deployments come back newest first, so the first one we
          // have not seen before is the deployment just triggered
          deployId = data.find((deployment) => !knownDeployIds.has(deployment.deployId))?.deployId;
        } catch {
          // transient errors just repeat the attempt until the deadline
        }
      }

      if (!deployId) {
        patchStep('triggerDeploy', { status: 'done' });
        return true;
      }

      let state: GitDeployState | undefined;
      const followDeadline = Date.now() + DEPLOY_FOLLOW_TIMEOUT_MS;

      while (Date.now() < followDeadline) {
        await delay(DEPLOY_POLL_MS);

        try {
          const { data } = await websitesApi.getWebsiteGitDeploys({ orgId, websiteId });
          const deployment = data.find((item) => item.deployId === deployId);
          state = deployment?.state ?? state;

          const { data: log } = await websitesApi.getWebsiteGitDeploymentLog({
            orgId,
            websiteId,
            deploymentId: deployId,
          });

          if (typeof log === 'string' && log) {
            setDeployLog(log);
          }

          if (deployment && (state === GitDeployState.complete || state === GitDeployState.failed)) {
            break;
          }
        } catch {
          // transient poll errors: keep following until the next tick
        }
      }

      const failed = state === GitDeployState.failed;

      if (failed) {
        patchStep('triggerDeploy', { status: 'error', error: '' });
        toast.error(
          <FormattedMessage
            id="websites.git_deploy.deploy_failed"
            defaultMessage="GitHub deployment failed. Check the deploy log for more details"
          />
        );
      } else if (state === GitDeployState.complete) {
        patchStep('triggerDeploy', { status: 'done' });
      }

      if (failed || state === GitDeployState.complete) {
        // leave the finished result (and the failure toast it triggered)
        // on screen briefly before the caller navigates on
        await delay(DEPLOY_RESULT_DISMISS_MS);
      }

      return !failed;
    },
    [toast]
  );

  return { steps, initSteps, runStep, deployLog, triggerAndFollowDeploy };
};

const dotBlink = keyframes`
  0%, 20% {
    opacity: 0;
   }

   40%, 100% {
    opacity: 1;
   }
`;

const stepPrefix = (status: GithubStepStatus) => css`
  flex-shrink: 0;
  width: 1.2em;
  color: ${status === 'error' ? '#f87171' : '#34d399'};
`;

const stepRow = (status: GithubStepStatus) => css`
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  min-height: 1.4em;
  line-height: 1.4em;
  opacity: ${status === 'pending' ? 0.4 : 1};
  color: ${status === 'error' ? '#f87171' : '#d1fae5'};
`;

const dot = (delayMs: number) => css`
  opacity: 0;
  animation: ${dotBlink} 1.2s infinite;
  animation-delay: ${delayMs}ms;
`;

const styles = {
  terminal: css`
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    width: 100%;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    background: #111827;
    color: #d1fae5;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.9rem;
  `,
  label: css`
    flex: 0 1 auto;
  `,
  error: css`
    flex: 1 1 auto;
    min-width: 0;
    white-space: pre-line;
    word-break: break-word;
  `,
  dots: css`
    display: inline-flex;
    width: 1.5em;
    margin-left: 0.1em;
  `,
  deployLog: css`
    width: 100%;
    max-height: 38vh;
    overflow-y: auto;
    margin: 0.5rem 0 0;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    background: #0b1220;
    color: #9ca3af;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.8rem;
    line-height: 1.45;
    white-space: pre-wrap;
    word-break: break-word;
  `,
};

// the live output of the deployment being followed, pinned to its newest
// line as the log streams in
const DeployLogPanel = ({ log }: { log: string }) => {
  const ref = useRef<HTMLPreElement>(null);

  useEffect(() => {
    const el = ref.current;

    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [log]);

  return (
    <pre ref={ref} css={styles.deployLog}>
      {log}
    </pre>
  );
};

/**
 * The overlay shown while a github-deploy website is being created: the
 * same shell as AddWebsiteAnimation's app-install overlay (icon above a
 * terminal-style step log), but driven by the real API calls of the
 * creation flow, so each line reports the actual call in flight and its
 * outcome instead of timer-cycled placeholder messages.
 */

export const AddWebsiteGithubProgress = ({ steps, deployLog }: Props) => (
  <div data-qa="addWebsiteGithubProgress">
    <Box
      d="column"
      j="center"
      a="center"
      position="fixed"
      top={'0'}
      left={'0'}
      w="100vw"
      h="100vh"
      zIndex={zIndex.WebsiteAnimation}
      bg="grey5"
    >
      <Box w={{ xs: 10, sm: 10, md: 6 }} h="100%" a="center" j="center" zIndex={zIndex.WebsiteAnimation}>
        <Box d="column" a="center" zIndex={zIndex.WebsiteAnimation} w="100%">
          <Icon name="github" size="3xl" marginBottom="xl" />
          <div css={styles.terminal}>
            {(steps ?? []).map((step) => (
              <div key={step.key} css={stepRow(step.status)}>
                <span css={stepPrefix(step.status)} aria-hidden="true">
                  {step.status === 'done' ? '✓' : step.status === 'error' ? '✗' : '$'}
                </span>
                <span css={styles.label}>{step.label}</span>
                {step.status === 'running' && (
                  <span css={styles.dots} aria-hidden="true">
                    <span css={dot(0)}>.</span>
                    <span css={dot(200)}>.</span>
                    <span css={dot(400)}>.</span>
                  </span>
                )}
                {step.status === 'error' && step.error && <span css={styles.error}>- {step.error}</span>}
              </div>
            ))}
          </div>
          {!!deployLog && <DeployLogPanel log={deployLog} />}
        </Box>
      </Box>
    </Box>
  </div>
);
